// AdminPrincipalMessages.js - Admin's private 1:1 chat with the Principal
// COLORS: #0D47A1, #2196F3, #90CAF9, #E3F2FD ONLY
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MessageCircle, Hand } from "lucide-react";
export default function AdminPrincipalMessages() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL || 'https://schoolcrm-fzcv.onrender.com';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  useEffect(() => {
    fetchMessages();
    // Poll every 6s so a new message from the Principal shows up live.
    const interval = setInterval(fetchMessages, 6000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  async function fetchMessages() {
    try {
      const res = await fetch(`${API_BASE}/api/principal-messages`);
      const j = await res.json();
      if (j.success) {
        setMessages(j.messages);
        // Mark principal's messages as read now that admin has viewed them
        fetch(`${API_BASE}/api/principal-messages/mark-read/admin`, {
          method: 'PUT'
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to load messages with principal', err);
    } finally {
      setLoading(false);
    }
  }
  async function sendMessage() {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/api/principal-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sender: 'admin',
          message: text
        })
      });
      const j = await res.json();
      if (j.success) {
        setInput('');
        fetchMessages();
      } else {
        alert('Failed to send message: ' + (j.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Failed to send message: ' + err.message);
    }
    setSending(false);
  }
  return <div style={{
    minHeight: "100vh",
    background: "#E3F2FD",
    fontFamily: "'Inter', sans-serif",
    padding: "24px"
  }}>
      {/* Back Button */}
      <button onClick={() => navigate('/admin')} style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#FFFFFF",
      border: "1px solid #90CAF9",
      padding: "10px 20px",
      borderRadius: 12,
      fontSize: 13,
      fontWeight: 600,
      color: "#0D47A1",
      cursor: "pointer",
      marginBottom: 20,
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)"
    }}>
        <span style={{
        fontSize: 18
      }}><ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
        Back to Dashboard
      </button>

      {/* Header */}
      <div style={{
      background: "linear-gradient(135deg, #0D47A1, #2196F3, #90CAF9)",
      padding: "20px 28px",
      borderRadius: 24,
      marginBottom: 24,
      boxShadow: "0 4px 20px rgba(13,71,161,0.2)",
      display: "flex",
      alignItems: "center",
      gap: 16
    }}>
        <div style={{
        width: 56,
        height: 56,
        borderRadius: 16,
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 28
      }}>
          <MessageCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />
        </div>
        <div>
          <div style={{
          fontWeight: 800,
          fontSize: 20,
          color: "#FFFFFF",
          letterSpacing: "-0.3px"
        }}>
            Principal Messages
          </div>
          <div style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.8)",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          fontWeight: 500
        }}>
            Private conversation with the Principal
          </div>
        </div>
      </div>

      {/* Chat panel */}
      <div style={{
      background: "#FFFFFF",
      borderRadius: 24,
      border: "1px solid #90CAF9",
      overflow: "hidden",
      boxShadow: "0 2px 12px rgba(0,0,0,0.04)"
    }}>
        {/* Thread */}
        <div style={{
        padding: "20px 24px",
        maxHeight: 480,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        background: "#FAFCFF"
      }}>
          {loading ? <div style={{
          textAlign: "center",
          padding: 24,
          color: "#0D47A1"
        }}>
              <i className="fas fa-spinner fa-pulse" style={{
            marginRight: 8
          }}></i>
              Loading conversation...
            </div> : messages.length === 0 ? <div style={{
          textAlign: "center",
          padding: 24,
          color: "#0D47A1",
          fontSize: 13
        }}>
              No messages yet. Start the conversation below <Hand className="inline w-[1em] h-[1em] align-[-0.125em]" />
            </div> : messages.map(m => {
          const isAdmin = m.sender === 'admin';
          return <div key={m._id} style={{
            alignSelf: isAdmin ? "flex-end" : "flex-start",
            maxWidth: "75%",
            display: "flex",
            flexDirection: "column",
            alignItems: isAdmin ? "flex-end" : "flex-start"
          }}>
                  <div style={{
              background: isAdmin ? "#0D47A1" : "#E3F2FD",
              color: isAdmin ? "#FFFFFF" : "#0D47A1",
              padding: "10px 16px",
              borderRadius: isAdmin ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
            }}>
                    {m.message}
                  </div>
                  <div style={{
              fontSize: 10,
              color: "#0D47A1",
              opacity: 0.7,
              marginTop: 4
            }}>
                    {isAdmin ? "You" : "Principal"} · {new Date(m.createdAt).toLocaleString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </div>
                </div>;
        })}
          <div ref={chatEndRef} />
        </div>

        {/* Composer */}
        <div style={{
        padding: "16px 24px",
        borderTop: "1px solid #90CAF9",
        display: "flex",
        gap: 10
      }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !sending) sendMessage();
        }} placeholder="Type a message to Principal..." style={{
          flex: 1,
          padding: "10px 14px",
          borderRadius: 12,
          border: "1px solid #90CAF9",
          fontSize: 13,
          outline: "none",
          color: "#0D47A1"
        }} />
          <button onClick={sendMessage} disabled={!input.trim() || sending} style={{
          background: !input.trim() || sending ? "#90CAF9" : "linear-gradient(135deg, #0D47A1, #2196F3)",
          color: "#FFFFFF",
          border: "none",
          padding: "10px 22px",
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 700,
          cursor: !input.trim() || sending ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8
        }}>
            <i className="fas fa-paper-plane"></i>
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
    </div>;
}